/**
 * Typed HTTP wrapper that the dashboard (and any future consumer) uses to
 * talk to the auth API.
 *
 * Authentication transport: the dashboard SPA carries the session JWT in
 * `Authorization: Bearer <jwt>` on every request. The JWT lives in
 * `localStorage` (see {@link "./token-store"}) and is auto-captured from
 * the response body on every session-issuing call (login, accept-invite,
 * create-workspace, switch-workspace). Cookies were removed in the
 * auth-redesign — there is no CSRF token to attach and no
 * `credentials: 'include'`.
 *
 * Responsibilities:
 *   - Inject `Authorization: Bearer <jwt>` from {@link tokenStore} when a
 *     token is present.
 *   - Capture `token` from `loginWithPassword`, `acceptInvitation`,
 *     `createWorkspace`, and `switchWorkspace` responses, persisting it
 *     for subsequent requests in the same SPA session.
 *   - Clear the stored token on `logout()` (success or failure).
 *   - Parse the standard error envelope (`{ error, message, request_id,
 *     details }`) and surface it as an {@link AuthClientError}.
 *   - Return typed responses for every endpoint.
 *
 * Configuration: `baseUrl` is read from `import.meta.env.VITE_API_BASE_URL`
 * by the default export; tests can construct an `AuthClient` with an
 * explicit base URL and `fetch` for full isolation.
 */
import { AuthClientError } from "./auth-client-error.js";
import { clearToken, getToken, setToken } from "./token-store.js";
export class AuthClient {
    baseUrl;
    fetchImpl;
    readToken;
    writeToken;
    removeToken;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/$/, "");
        // Bind so tests can pass `globalThis.fetch` without losing `this`.
        this.fetchImpl = opts.fetch ?? ((input, init) => fetch(input, init));
        this.readToken = opts.getToken ?? getToken;
        this.writeToken = opts.setToken ?? setToken;
        this.removeToken = opts.clearToken ?? clearToken;
    }
    // ----- Auth — email + password (ENG-35588) -----
    /**
     * Sign up with email + password. WorkOS creates the user and emails a
     * 6-digit verification OTC. No session is issued yet — the dashboard
     * should navigate to `/verify-email?email=...` on success.
     *
     * Errors surfaced as {@link AuthClientError} with `code`:
     *   - `AUTH_EMAIL_IN_USE` (409) — verbatim WorkOS message in `.message`
     *   - `AUTH_WEAK_PASSWORD` / `AUTH_BREACHED_PASSWORD` (400)
     */
    register = (body) => this.request("POST", "/api/auth/signup", body);
    /**
     * Submit the 6-digit OTC the user typed on `/verify-email`. On success
     * no session is issued — the dashboard navigates back to `/login`.
     *
     * Errors:
     *   - `AUTH_INVALID_OTC` (400)
     *   - `AUTH_OTC_EXPIRED` (400)
     */
    verifyEmail = (body) => this.request("POST", "/api/auth/verify-email", body);
    /**
     * Resend the verification OTC. Returns `{ status: "sent" }` whether or
     * not the email is known to WorkOS (anti-enumeration). Rate-limited
     * provider-side (3 / 10min / email — 429 surfaces as `RATE_LIMITED`).
     */
    resendVerification = (body) => this.request("POST", "/api/auth/resend-verification", body);
    // TODO(magic-link): preserved for the day product re-enables magic-link.
    // The corresponding API endpoints are commented out (404). See
    // `aidlc-docs/inception/requirements/auth-redesign.md § FR-37`.
    //
    // requestMagicLink = (body: { email: string }): Promise<{ pending: true }> =>
    //   this.request("POST", "/api/auth/login/magic", body);
    //
    // verifyMagicCode = (body: {
    //   email: string;
    //   code: string;
    // }): Promise<SessionEstablished> => {
    //   const result = await this.request<SessionEstablished>(
    //     "POST", "/api/auth/login/magic/verify", body);
    //   this.writeToken(result.token);
    //   return result;
    // };
    /**
     * Email + password login. On success captures the session JWT into
     * `localStorage` so every subsequent request authenticates via
     * `Authorization: Bearer …`.
     *
     * The `workspace` field is `null` when the user is workspace-less
     * (ENG-35588) — the caller should navigate to `/no-workspace`.
     */
    loginWithPassword = async (body) => {
        const result = await this.request("POST", "/api/auth/login/password", body);
        this.writeToken(result.token);
        return result;
    };
    /**
     * Mint a long-lived MCP access token for the current user
     * (`POST /api/auth/mcp-token`). The token is displayed ONCE in the
     * dashboard for pasting into an MCP client. It is intentionally NOT
     * written to localStorage — the browser session stays untouched.
     */
    mintMcpToken = async () => {
        return this.request("POST", "/api/auth/mcp-token");
    };
    /**
     * Begin Google OAuth by full-page navigating to the API's `/login/google`
     * endpoint. The API handles the WorkOS redirect dance and ultimately
     * lands the browser back at `${dashboardOrigin}/auth/handoff#token=…`
     * (see the `/auth/handoff` route in the dashboard).
     */
    beginGoogleLogin = () => {
        if (typeof window === "undefined")
            return;
        window.location.assign(`${this.baseUrl}/api/auth/login/google`);
    };
    /**
     * Logs out at the API (revokes the upstream IdP session) and clears
     * the locally-stored JWT. The token is cleared even when the API call
     * fails so the SPA can never end up "logged in client-side, logged out
     * server-side".
     */
    logout = async () => {
        try {
            await this.request("POST", "/api/auth/logout");
        }
        finally {
            this.removeToken();
        }
    };
    // ----- Current state -----
    /** Fetch `/api/me`. Returns `null` on 401 instead of throwing. */
    me = async () => {
        try {
            return await this.request("GET", "/api/me");
        }
        catch (err) {
            if (err instanceof AuthClientError && err.status === 401) {
                // Token is missing or expired — drop it so subsequent calls don't
                // keep hitting 401 with a stale bearer header.
                this.removeToken();
                return null;
            }
            throw err;
        }
    };
    // ----- Workspaces -----
    listWorkspaces = () => this.request("GET", "/api/workspaces");
    /**
     * Creates a workspace. If the caller was workspace-less (onboarding,
     * ENG-35588) the response carries a rotated `token` scoped to the new
     * workspace — we capture it into `localStorage` so the dashboard's
     * subsequent calls authenticate against the new tenant. If the caller
     * already had a workspace, no rotation is required.
     */
    createWorkspace = async (body) => {
        const result = await this.request("POST", "/api/workspaces", body);
        if (result.token)
            this.writeToken(result.token);
        return result;
    };
    getWorkspace = (slug) => this.request("GET", `/api/workspaces/${encodeURIComponent(slug)}`);
    updateWorkspace = (slug, body) => this.request("PATCH", `/api/workspaces/${encodeURIComponent(slug)}`, body);
    /**
     * Switches the active workspace. The API mints a fresh JWT scoped to
     * the target workspace; we capture it so the next request runs against
     * the new tenant.
     */
    switchWorkspace = async (body) => {
        const result = await this.request("POST", "/api/workspaces/switch", body);
        this.writeToken(result.token);
        return result;
    };
    // ----- Members -----
    listMembers = (slug) => this.request("GET", `/api/workspaces/${encodeURIComponent(slug)}/members`);
    // `role` is a built-in workspace role key OR a custom `RoleDefinition.id`;
    // the server validates it against the workspace's built-in + custom roles.
    changeRole = (slug, memberId, role) => this.request("PATCH", `/api/workspaces/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberId)}`, { role });
    removeMember = (slug, memberId) => this.request("DELETE", `/api/workspaces/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberId)}`);
    // ----- Org members (account tier) -----
    /** Lists the caller's organization members + their org roles. */
    listOrgMembers = () => this.request("GET", "/api/org/members");
    // `memberId` is an `OrgMembership.id`. The server enforces SELF_ROLE_CHANGE
    // and LAST_ORG_ADMIN.
    changeOrgMemberRole = (memberId, role) => this.request("PATCH", `/api/org/members/${encodeURIComponent(memberId)}`, { role });
    /** Lists every workspace in the caller's organization (org-admin overview). */
    listOrgWorkspaces = () => this.request("GET", "/api/org/workspaces");
    // ----- Invitations -----
    listPendingInvitations = (slug) => this.request("GET", `/api/workspaces/${encodeURIComponent(slug)}/invitations`);
    // `role` is a built-in workspace role key OR a custom `RoleDefinition.id`;
    // the server validates it. Defaults to `member` server-side when omitted.
    inviteMember = (slug, body) => this.request("POST", `/api/workspaces/${encodeURIComponent(slug)}/invitations`, body);
    revokeInvitation = (slug, invitationId) => this.request("DELETE", `/api/workspaces/${encodeURIComponent(slug)}/invitations/${encodeURIComponent(invitationId)}`);
    previewInvitation = (params) => this.request("GET", `/api/auth/join/preview?token=${encodeURIComponent(params.token)}`);
    /**
     * Accept an invitation. Two response shapes:
     *   - Session minted immediately: `{ token, workspace, role }` —
     *     captured into `localStorage`; the dashboard can navigate straight
     *     to `/w/<slug>/`.
     *   - `{ pending: true }` — magic-link round-trip required (the user is
     *     new and needs to verify their email first).
     */
    acceptInvitation = async (body) => {
        const result = await this.request("POST", "/api/auth/join", body);
        if ("token" in result)
            this.writeToken(result.token);
        return result;
    };
    // ----- Notifications -----
    /** The current user's recent notifications + unread count. */
    listNotifications = () => this.request("GET", "/api/notifications");
    /** Mark all of the user's notifications as read; returns how many changed. */
    markNotificationsRead = () => this.request("POST", "/api/notifications/read");
    // ----- API keys -----
    listApiKeys = (slug) => this.request("GET", `/api/workspaces/${encodeURIComponent(slug)}/api-keys`);
    createApiKey = (slug, body) => this.request("POST", `/api/workspaces/${encodeURIComponent(slug)}/api-keys`, body);
    updateApiKey = (slug, keyId, body) => this.request("PATCH", `/api/workspaces/${encodeURIComponent(slug)}/api-keys/${encodeURIComponent(keyId)}`, body);
    revokeApiKey = (slug, keyId) => this.request("DELETE", `/api/workspaces/${encodeURIComponent(slug)}/api-keys/${encodeURIComponent(keyId)}`);
    // ----- Core -----
    async request(method, path, body) {
        const headers = { Accept: "application/json" };
        if (body !== undefined)
            headers["Content-Type"] = "application/json";
        const token = this.readToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        // 204 No Content — typical for DELETE / mutating ops without a body.
        if (res.status === 204)
            return undefined;
        const text = await res.text();
        const parsed = text.length > 0 ? safeParse(text) : undefined;
        if (!res.ok) {
            const envelope = (parsed ?? {});
            throw new AuthClientError({
                status: res.status,
                code: envelope.error ?? `HTTP_${res.status}`,
                message: envelope.message ?? res.statusText ?? "Request failed",
                requestId: envelope.request_id ?? "",
                details: envelope.details,
            });
        }
        return parsed;
    }
}
function safeParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
/**
 * Default singleton wired to `VITE_API_BASE_URL`.
 *
 * Apps with non-Vite build tooling should construct their own `AuthClient`
 * with an explicit `baseUrl`.
 */
function readDefaultBaseUrl() {
    // `import.meta.env` is Vite-specific; guard via a cast so this file is
    // safe to import in Node test runners that lack a Vite env shim.
    const env = import.meta.env;
    return env?.VITE_API_BASE_URL ?? "";
}
export const authClient = new AuthClient({ baseUrl: readDefaultBaseUrl() });
//# sourceMappingURL=auth-client.js.map